import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@scio3/ui';
import { ADMIN_ROLES, type AdminRole, type AdminUser } from '@scio3/shared-types';
import { STAFF_UPDATE_ADMIN_USER_ROLE } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';

interface Props {
  user: AdminUser | null;
  onOpenChange: (open: boolean) => void;
}

interface UpdateResult {
  staffUpdateAdminUserRole: AdminUser;
}

export function EditRoleDialog({ user, onOpenChange }: Props) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.adminUser);
  const [role, setRole] = useState<AdminRole>(user?.role ?? ADMIN_ROLES.ENGINEER);

  useEffect(() => {
    if (user) setRole(user.role);
  }, [user]);

  const [updateMutation, { loading }] = useMutation<UpdateResult>(STAFF_UPDATE_ADMIN_USER_ROLE, {
    refetchQueries: ['staffAdminUsers'],
  });

  // Block self-demote from root — UX-level enforcement; backend should reject too.
  const isSelfDemoteFromRoot =
    !!user && !!me && user.email === me.email && user.role === 'root' && role !== 'root';

  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: loading,
    isDirty: !!user && role !== user.role,
    validationError: isSelfDemoteFromRoot ? t('You cannot demote yourself from root.') : '',
  });

  const handleSubmit = async () => {
    if (!user) return;
    try {
      await updateMutation({ variables: { email: user.email, role } });
      toast.success(t('Role updated'));
      onOpenChange(false);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? t('Unknown error');
      toast.error(t('Role update failed'), { description: msg });
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-role-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Change role')}</DialogTitle>
        </DialogHeader>

        {user ? (
          <div className="flex flex-col gap-4 py-2">
            <div className="rounded-md bg-muted px-3 py-2 text-sm font-medium">{user.email}</div>
            <div className="space-y-2">
              <Label>{t('Role')}</Label>
              <RadioGroup
                value={role}
                onValueChange={(v) => setRole(v as AdminRole)}
                className="grid grid-cols-2 gap-2"
              >
                <RoleOption value={ADMIN_ROLES.ROOT} label={t('Root')} />
                <RoleOption value={ADMIN_ROLES.ENGINEER} label={t('Engineer')} />
                <RoleOption value={ADMIN_ROLES.SUPPORT} label={t('Support')} />
                <RoleOption value={ADMIN_ROLES.READONLY} label={t('Read only')} />
              </RadioGroup>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('Cancel')}</Button>
          </DialogClose>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={submitDisabled ? 0 : -1}>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    data-testid="edit-role-submit"
                  >
                    {loading ? t('Saving…') : t('Save')}
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledTooltip ? <TooltipContent>{disabledTooltip}</TooltipContent> : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleOption({ value, label }: { value: AdminRole; label: string }) {
  const id = `edit-role-${value}`;
  return (
    <div className="flex items-center gap-2 rounded-md border px-3 py-2">
      <RadioGroupItem id={id} value={value} />
      <Label htmlFor={id} className="cursor-pointer">
        {label}
      </Label>
    </div>
  );
}
